import DINProRegularUrl from '@/assets/fonts/DINPro/DINPro400.ttf?url'
import DINProBoldUrl from '@/assets/fonts/DINPro-Bold/DINProBold700.ttf?url'
import DINProMediumUrl from '@/assets/fonts/DINPro-Medium/DINProMedium500.ttf?url'
import logoUrl from '@/assets/logos/value-app-logo.png?url'
import type { SummaryListItemType } from '@/types'
import i18n from '@/i18n'
import type { PdfMakeStatic } from 'pdfmake/build/pdfmake'

export type PdfNode = Record<string, unknown>
export type ListSectionKind =
  | 'default'
  | 'serviceRole'
  | 'serviceBasic'
  | 'servicePhases'
  | 'serviceAdditional'
  | 'setupUsage'
  | 'setupRisk'
  | 'setupCost'
  | 'general'
  | 'organisation'

const t = (key: string): string => (i18n.global as { t: (k: string) => string }).t(key)

let fontSetupPromise: Promise<void> | null = null
let pdfMakePromise: Promise<PdfMakeStatic> | null = null
let logoDataUri: string | null = null

export function getCurrentLocale(): string {
  const globalWithLocale = i18n.global as {
    locale?: string | { value?: string }
  }
  const locale = globalWithLocale.locale

  if (typeof locale === 'string' && locale.trim().length > 0) {
    return locale
  }

  if (locale && typeof locale === 'object' && typeof locale.value === 'string' && locale.value.trim().length > 0) {
    return locale.value
  }

  return 'en'
}

export async function getPdfMake(): Promise<PdfMakeStatic> {
  if (pdfMakePromise) {
    return pdfMakePromise
  }

  pdfMakePromise = Promise.all([import('pdfmake/build/pdfmake'), import('pdfmake/build/vfs_fonts')])
    .then(([pdfMakeModule, vfsModule]) => {
      const pdfMake = pdfMakeModule.default
      const pdfDefaultVfs = vfsModule.default

      if (pdfMake.addVirtualFileSystem) {
        pdfMake.addVirtualFileSystem(pdfDefaultVfs)
      } else {
        pdfMake.vfs = { ...(pdfMake.vfs ?? {}), ...pdfDefaultVfs }
      }

      return pdfMake
    })
    .catch((error) => {
      pdfMakePromise = null
      throw error
    })

  return pdfMakePromise
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  const chunkSize = 0x8000
  let binary = ''
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize)
    binary += String.fromCharCode(...chunk)
  }
  return btoa(binary)
}

async function loadFontBase64(url: string): Promise<string> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to load font from ${url}`)
  }
  return arrayBufferToBase64(await response.arrayBuffer())
}

async function loadImageDataUri(url: string, mimeType: string): Promise<string> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to load image from ${url}`)
  }
  return `data:${mimeType};base64,${arrayBufferToBase64(await response.arrayBuffer())}`
}

export async function setupPdfMakeFonts(): Promise<void> {
  if (fontSetupPromise) {
    return fontSetupPromise
  }

  fontSetupPromise = (async () => {
    const pdfMake = await getPdfMake()

    const [regularBase64, boldBase64, mediumBase64, logoBase64] = await Promise.all([
      loadFontBase64(DINProRegularUrl),
      loadFontBase64(DINProBoldUrl),
      loadFontBase64(DINProMediumUrl),
      loadImageDataUri(logoUrl, 'image/png'),
    ])

    logoDataUri = logoBase64

    const customVfs = {
      'DINPro400.ttf': regularBase64,
      'DINProBold700.ttf': boldBase64,
      'DINProMedium500.ttf': mediumBase64,
    }

    if (pdfMake.addVirtualFileSystem) {
      pdfMake.addVirtualFileSystem(customVfs)
    } else {
      pdfMake.vfs = { ...(pdfMake.vfs ?? {}), ...customVfs }
    }

    const customFonts = {
      DINPro: {
        normal: 'DINPro400.ttf',
        bold: 'DINProMedium500.ttf',
        italics: 'DINPro400.ttf',
        bolditalics: 'DINProMedium500.ttf',
      },
      DINProBold: {
        normal: 'DINProBold700.ttf',
        bold: 'DINProBold700.ttf',
        italics: 'DINProBold700.ttf',
        bolditalics: 'DINProBold700.ttf',
      },
    }

    if (pdfMake.addFonts) {
      pdfMake.addFonts(customFonts)
    } else {
      pdfMake.fonts = { ...(pdfMake.fonts ?? {}), ...customFonts }
    }
  })().catch((error) => {
    fontSetupPromise = null
    throw error
  })

  return fontSetupPromise
}

export function getLogoDataUri(): string | null {
  return logoDataUri
}

export function divider(): PdfNode {
  return {
    canvas: [
      {
        type: 'line',
        x1: 0,
        y1: 0,
        x2: 515,
        y2: 0,
        lineWidth: 0.7,
        lineColor: '#D4D4D8',
      },
    ],
    margin: [0, 0, 0, 0],
  }
}

export function normalizeText(raw: string): string {
  return raw
    .replace(/\r\n/g, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim()
}

export function preventTitleWidow(raw: string): string {
  const normalized = normalizeText(raw)
  return normalized
    .split('\n')
    .map((line) => line.replace(/\s+([^\s]+)\s*$/, '\u00A0$1'))
    .join('\n')
}

export function labelStyle(additionalClass: string): string {
  if (additionalClass === 'title-item') return 'titleItemLabel'
  if (additionalClass === 'highlight-item' || additionalClass === 'dark-title') return 'itemLabelStrong'
  if (additionalClass === 'semi-highlight-item') return 'itemLabelSemiStrong'
  return 'itemLabel'
}

export function valueStyle(additionalClass: string, sectionKind: ListSectionKind): string {
  if (additionalClass === 'title-item') return 'titleItemValue'
  if (additionalClass === 'highlight-item' || additionalClass === 'dark-title') return 'itemValueStrong'
  if (additionalClass === 'semi-highlight-item') return 'itemValueSemiStrong'
  if (additionalClass === 'muted-value') return 'itemValueMuted'
  const isBasicServicePhaseRow = sectionKind === 'servicePhases' && additionalClass !== 'highlight-item'
  const isAdditionalServicePhaseRow =
    sectionKind === 'serviceAdditional' && !['highlight-item', 'semi-highlight-item'].includes(additionalClass)
  if (isBasicServicePhaseRow || isAdditionalServicePhaseRow) return 'phaseItemValue'
  return 'itemValue'
}

export interface ColumnWidths {
  label: string
  value: string
}

const DEFAULT_COLUMN_WIDTHS: ColumnWidths = { label: '40%', value: '60%' }

export function createRowBlocks(
  items: SummaryListItemType[],
  sectionKind: ListSectionKind,
  columnWidths: ColumnWidths = DEFAULT_COLUMN_WIDTHS
): PdfNode[][] {
  const rowBlocks: PdfNode[][] = []

  for (const item of items) {
    const block: PdfNode[] = []
    const rawAdditionalClass = item.additionalClass ?? ''
    const additionalClass =
      sectionKind === 'setupRisk' && rawAdditionalClass === 'title-item' ? '' : rawAdditionalClass

    if (additionalClass === 'justification-text') {
      block.push({
        text: normalizeText(item.title),
        style: 'justificationText',
        margin: [0, -2, 0, 8],
      })
      rowBlocks.push(block)
      continue
    }

    if (additionalClass !== 'title-item') {
      block.push(divider())
    } else {
      block.push({ text: '', margin: [0, 11, 0, 0] })
    }

    const titleText = normalizeText(item.title)
    const valueText = normalizeText(String(item.value ?? ''))

    if (additionalClass === 'text-block') {
      if (valueText.length > 0) {
        block.push({
          columns: [
            { width: columnWidths.label, text: titleText, style: 'itemLabel' },
            { width: columnWidths.value, text: '' },
          ],
          columnGap: 10,
          margin: [0, 8, 0, 0],
        })
        block.push({
          text: valueText,
          style: 'textBlockValue',
          margin: [0, 6, 0, 8],
        })
      } else {
        block.push({
          columns: [
            { width: columnWidths.label, text: titleText, style: 'itemLabel' },
            { width: columnWidths.value, text: '', style: 'itemValue', alignment: 'right' },
          ],
          columnGap: 10,
          margin: [0, 8, 0, 8],
        })
      }
    } else {
      block.push({
        columns: [
          { width: columnWidths.label, text: titleText, style: labelStyle(additionalClass) },
          {
            width: columnWidths.value,
            text: valueText,
            style: valueStyle(additionalClass, sectionKind),
            alignment: 'right',
          },
        ],
        columnGap: 10,
        margin: [0, 8, 0, 8],
      })
    }

    if (item.remark) {
      block.push({
        text: normalizeText(item.remark),
        style: 'remarkText',
        margin: [0, -2, 0, 10],
      })
    }

    rowBlocks.push(block)
  }

  return rowBlocks
}

export function createListSection(
  title: string,
  list: SummaryListItemType[] | undefined,
  sectionKind: ListSectionKind = 'default',
  columnWidths?: ColumnWidths,
  titleStyle: string = 'sectionTitle'
): PdfNode | null {
  if (!title || !list?.length) {
    return null
  }

  const rowBlocks = createRowBlocks(list, sectionKind, columnWidths)
  const preferred = sectionKind === 'setupUsage' ? 3 : 4
  const rowsToKeepWithTitle = Math.max(1, Math.min(preferred, list.length))

  return {
    margin: [0, 0, 0, 30],
    stack: [
      {
        unbreakable: true,
        stack: [
          { text: preventTitleWidow(title), style: titleStyle },
          ...rowBlocks.slice(0, rowsToKeepWithTitle).flat(),
        ],
      },
      ...rowBlocks.slice(rowsToKeepWithTitle).flat(),
      divider(),
    ],
  }
}

export function trimTrailingSectionSpacing(sections: PdfNode[]): PdfNode[] {
  if (sections.length === 0) {
    return sections
  }

  const lastSection = sections[sections.length - 1] as PdfNode & { margin?: unknown }
  if (!Array.isArray(lastSection.margin) || lastSection.margin.length !== 4) {
    return sections
  }

  const trimmedMargin = [...lastSection.margin]
  trimmedMargin[3] = 0

  return [...sections.slice(0, -1), { ...lastSection, margin: trimmedMargin }]
}

export function buildFooter(
  wtSummary: (...path: string[]) => string,
  today: string
): (currentPage: number, pageCount: number) => PdfNode {
  const pageLabel = wtSummary('pdf', 'description', 'page') || 'Page'
  const ofLabel = wtSummary('pdf', 'description', 'of') || 'of'

  return (currentPage: number, pageCount: number) => ({
    margin: [40, 0, 40, 0],
    fontSize: 8,
    color: '#737373',
    columns: [
      logoDataUri ? { image: logoDataUri, width: 80 } : { text: 'Value App', width: 80, margin: [0, 11, 0, 0] },
      {
        text: `${pageLabel} ${currentPage} ${ofLabel} ${pageCount}`,
        alignment: 'center',
        width: '*',
        margin: [0, 11, 0, 0],
      },
      { text: today, alignment: 'right', width: 80, margin: [0, 11, 0, 0] },
    ],
  })
}

export function getSharedStyles(): Record<string, PdfNode> {
  return {
    documentTitle: { fontSize: 20, font: 'DINProBold', color: '#27272A' },
    projectTitle: { fontSize: 15, font: 'DINProBold', color: '#3F3F46' },
    partTitle: { fontSize: 18, font: 'DINProBold', color: '#27272A' },
    sectionTitle: { fontSize: 14, font: 'DINProBold', color: '#3F3F46', margin: [0, 0, 0, 8] },
    subSectionTitle: { fontSize: 11, font: 'DINProBold', color: '#3F3F46', margin: [0, 0, 0, 8] },
    itemLabel: { color: '#737373' },
    itemLabelStrong: { color: '#404040', bold: true },
    itemLabelSemiStrong: { color: '#52525B', bold: true },
    titleItemLabel: { color: '#404040', bold: true, fontSize: 14 },
    itemValue: { color: '#404040' },
    itemValueStrong: { color: '#404040', bold: true },
    itemValueSemiStrong: { color: '#52525B', bold: true },
    titleItemValue: { color: '#404040', bold: true, fontSize: 14 },
    phaseItemValue: { color: '#737373' },
    itemValueMuted: { color: '#A3A3A3' },
    textBlockValue: { color: '#52525B', fontSize: 9, lineHeight: 1.35 },
    justificationText: { color: '#A3A3A3', fontSize: 8 },
    remarkText: { color: '#737373', fontSize: 9, italics: true },
    versionText: { color: '#737373', fontSize: 8 },
  }
}

export function getPdfAuthor(): string {
  return t('general.pdf_author')
}
